import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import { myArRequests, respondArRequest } from '~/lib/ar';
import { getSession } from '~/lib/auth';
import { getConnections, removeConnection, respondConnection } from '~/lib/connections';
import { buildHead } from '~/seo/site';
import { Button } from '~/ui/components/button';

export const Route = createFileRoute('/inbox')({
  head: () =>
    buildHead({ title: 'Inbox', description: 'Friend requests and A&R interest.', path: '/inbox' }),
  beforeLoad: async ({ location }) => {
    const session = await getSession();
    if (!session) throw redirect({ to: '/auth/sign-in', search: { next: location.pathname } });
  },
  component: Inbox,
});

function Avatar({ handle, url }: { handle: string; url: string | null }) {
  return url ? (
    <img src={url} alt="" className="size-8 rounded-full object-cover" />
  ) : (
    <span className="flex size-8 items-center justify-center rounded-full bg-muted text-xs">
      {handle[0]?.toUpperCase()}
    </span>
  );
}

function Inbox() {
  const qc = useQueryClient();
  const { data: conns } = useQuery({ queryKey: ['connections'], queryFn: getConnections });
  const { data: arData } = useQuery({ queryKey: ['ar-requests'], queryFn: myArRequests });

  const respondConn = useMutation({
    mutationFn: ({ userId, accept }: { userId: string; accept: boolean }) =>
      respondConnection(userId, accept),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['connections'] }),
  });
  const cancelConn = useMutation({
    mutationFn: (userId: string) => removeConnection(userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['connections'] }),
  });
  const respondAr = useMutation({
    mutationFn: ({ id, accept }: { id: string; accept: boolean }) => respondArRequest(id, accept),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ar-requests'] }),
  });

  const incoming = conns?.incoming ?? [];
  const outgoing = conns?.outgoing ?? [];
  const friends = conns?.friends ?? [];
  const arReqs = arData?.items ?? [];
  const empty = incoming.length + outgoing.length + friends.length + arReqs.length === 0;

  return (
    <section className="mx-auto max-w-2xl px-4 py-10 space-y-8">
      <header className="space-y-1">
        <h1 className="text-3xl font-bold tracking-tight">Inbox</h1>
        <p className="text-sm text-muted-foreground">
          Friend requests, your connections, and A&R interest.
        </p>
      </header>

      {empty && (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Nothing here yet. Add producers as friends from their profile.
        </div>
      )}

      {incoming.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Friend requests
          </h2>
          {incoming.map((u) => (
            <div
              key={u.userId}
              className="flex items-center gap-3 rounded-lg border border-border bg-card p-3"
            >
              <Avatar handle={u.handle} url={u.avatarUrl} />
              <Link
                to="/$username"
                params={{ username: u.handle }}
                className="flex-1 font-medium hover:underline"
              >
                @{u.handle}
              </Link>
              <Button
                size="sm"
                disabled={respondConn.isPending}
                onClick={() => respondConn.mutate({ userId: u.userId, accept: true })}
              >
                Accept
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={respondConn.isPending}
                onClick={() => respondConn.mutate({ userId: u.userId, accept: false })}
              >
                Decline
              </Button>
            </div>
          ))}
        </div>
      )}

      {arReqs.filter((r) => r.status === 'pending').length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            A&R interest
          </h2>
          {arReqs
            .filter((r) => r.status === 'pending')
            .map((r) => (
              <div
                key={r.id}
                className="rounded-lg border border-violet-500/30 bg-violet-500/5 p-3 space-y-2"
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-violet-300">@{r.arHandle}</span>
                  <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    A&R
                  </span>
                </div>
                <p className="text-sm">{r.message}</p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    disabled={respondAr.isPending}
                    onClick={() => respondAr.mutate({ id: r.id, accept: true })}
                  >
                    Accept
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={respondAr.isPending}
                    onClick={() => respondAr.mutate({ id: r.id, accept: false })}
                  >
                    Decline
                  </Button>
                </div>
              </div>
            ))}
        </div>
      )}

      {outgoing.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Pending sent
          </h2>
          {outgoing.map((u) => (
            <div
              key={u.userId}
              className="flex items-center gap-3 rounded-lg border border-border p-3"
            >
              <Avatar handle={u.handle} url={u.avatarUrl} />
              <span className="flex-1 text-sm">@{u.handle}</span>
              <Button size="sm" variant="ghost" onClick={() => cancelConn.mutate(u.userId)}>
                Cancel
              </Button>
            </div>
          ))}
        </div>
      )}

      {friends.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Friends ({friends.length})
          </h2>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {friends.map((u) => (
              <Link
                key={u.userId}
                to="/$username"
                params={{ username: u.handle }}
                className="flex items-center gap-2 rounded-lg border border-border p-2 hover:border-primary"
              >
                <Avatar handle={u.handle} url={u.avatarUrl} />
                <span className="truncate text-sm">@{u.handle}</span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
